import {featureFlags, FeatureFlagValues} from "@buildwithflux/core";
import {areWeTestingWithJest, isDevEnv, Unsubscriber} from "@buildwithflux/shared";
import {create as createMutative} from "mutative";
import {mapValues} from "lodash";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";

import {
    AbstractLaunchDarklyService,
    ContextSyncState,
    FeatureFlagLifecycleState,
    FlagSet,
    FreshnessState,
} from "./LaunchDarklyService";

export type FeatureFlagsState = {
    sourceUnsubscriber: Unsubscriber;
    lifecycleUnsubscriber: Unsubscriber;
    contextSync: ContextSyncState;
    freshness: FreshnessState;
    /** Duration in ms from "cached" to "fresh" state. Only set when freshness transitions to "fresh". */
    cachedToFreshDurationMs?: number;
} & FeatureFlagValues;

export type FeatureFlagsStore = StoreApi<FeatureFlagsState>;
export type UseFeatureFlagsStore = UseBoundStore<FeatureFlagsStore>;

function getFluxFeatureFlagName(remoteName: string): keyof FeatureFlagValues | undefined {
    const result = Object.entries(featureFlags).find(([_fluxName, ldName]) => ldName === remoteName)?.[0];
    // NOTE: This is a safe cast, the compiler just doesn't know it because of the type of Object.entries
    return result as keyof FeatureFlagValues | undefined;
}

export function mergeFlags(flags: FlagSet, currentState: FeatureFlagsState): FeatureFlagsState {
    if (!flags) return currentState;
    return createMutative(currentState, (draftState) => {
        for (const [flagName, flagValue] of Object.entries(flags)) {
            const fluxName = getFluxFeatureFlagName(flagName);
            if (!fluxName) {
                // This is because this feature flag is not used from the frontend - this feature flag can be safely
                // ignored
            } else {
                // Some how we need to do a cast here even Typescript correctly inferred that `fluxName` is
                // of type "keyof FeatureFlagValues"
                (draftState as any)[fluxName] = flagValue;
            }
        }
    });
}

function emptyFlags(): Partial<FeatureFlagValues> {
    return mapValues(featureFlags, (_o) => undefined);
}

export function getInitialFlagValues(featureFlags: AbstractLaunchDarklyService): FeatureFlagValues {
    const allFlags = featureFlags.allFlags();
    const result = emptyFlags();
    if (allFlags != null) {
        for (const [flagName, flagValue] of Object.entries(allFlags)) {
            const fluxName: keyof FeatureFlagValues | undefined = getFluxFeatureFlagName(flagName);
            if (fluxName) {
                result[fluxName] = flagValue as undefined;
            }
        }
    }

    // TODO: This is NOT a safe cast: we are initializing every flag as undefined, including ones we type as an object!
    return result as FeatureFlagValues;
}

export function createFeatureFlagsStore(launchDarklyService: AbstractLaunchDarklyService): UseFeatureFlagsStore {
    return create<FeatureFlagsState>()(
        devtools(
            (set, get) => {
                const sourceUnsubscriber = launchDarklyService.subscribeToFlags((flags) =>
                    set(mergeFlags(flags, get()), false, "updateFromLaunchDarkly"),
                );

                const lifecycleUnsubscriber = launchDarklyService.subscribeToLifecycleState(
                    (lifecycleState: FeatureFlagLifecycleState) =>
                        set(
                            createMutative((state) => {
                                state.contextSync = lifecycleState.contextSync;
                                state.freshness = lifecycleState.freshness;
                                state.cachedToFreshDurationMs = lifecycleState.cachedToFreshDurationMs;
                            }),
                            false,
                            "updateLifecycleState",
                        ),
                );

                const initialFlagValues = getInitialFlagValues(launchDarklyService);
                const initialLifecycleState = launchDarklyService.getLifecycleState();

                return {
                    sourceUnsubscriber,
                    lifecycleUnsubscriber,
                    contextSync: initialLifecycleState.contextSync,
                    freshness: initialLifecycleState.freshness,
                    ...initialFlagValues,
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "FeatureFlagsStore"},
        ),
    );
}
